import { useEffect, useState } from 'react'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { GatedButton } from '../ui/GatedButton'
import { TextArea } from '../ui/Input'
import { useToast } from '../../contexts/ToastContext'
import { useSendModeratorMessage } from '../../hooks/useFriends'
import { apiError } from '../../lib/api/client'

interface ModeratorMessageDialogProps {
  open: boolean
  onClose: () => void
}

/** "Message a moderator" compose (COTC_FriendsApprovals R3). */
export function ModeratorMessageDialog({ open, onClose }: ModeratorMessageDialogProps) {
  const { flash } = useToast()
  const send = useSendModeratorMessage()
  const [text, setText] = useState('')
  const [sent, setSent] = useState(false)

  useEffect(() => { if (open) { setText(''); setSent(false) } }, [open])
  if (!open) return null

  async function submit() {
    try {
      await send.mutateAsync({ text: text.trim() })
      setSent(true)
    } catch (err) {
      flash(apiError(err).message)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Message a moderator">
      {sent ? (
        <div className="rounded-card bg-moss-bg p-5">
          <p className="text-[14px] font-medium text-moss">Sent.</p>
          <p className="mt-2 text-[13px] leading-relaxed text-ink-muted">
            Your message was received and queued for moderator review. No email reply or response time is confirmed.
          </p>
          <div className="mt-5 flex justify-end">
            <Button variant="ink" onClick={onClose}>Done</Button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-[13px] leading-relaxed text-ink-muted">
            Tell us what is going on. Your message enters the moderator review queue; this is not an emergency channel.
          </p>
          <TextArea
            className="mt-3"
            label="Moderator message"
            rows={5}
            maxLength={1000}
            placeholder="What would you like a moderator to know?"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="mt-5 flex justify-between">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <GatedButton gated={text.trim().length === 0 || send.isPending} hint="Write your message first." variant="ink" onClick={submit}>
              Send to a moderator
            </GatedButton>
          </div>
        </>
      )}
    </Modal>
  )
}
